import React, { useEffect, useMemo, useRef } from "react";

interface HTMLPreviewProps {
  htmlContent?: string;
}

const PLACEHOLDER_HIGHLIGHT_STYLE =
  "background: yellow; padding: 0 2px; border-radius: 2px;";

const placeholderMap: Record<string, string> = {
  username: `<span style="${PLACEHOLDER_HIGHLIGHT_STYLE}">[username]</span>`,
  points: `<span style="${PLACEHOLDER_HIGHLIGHT_STYLE}">[points]</span>`,
  balance: `<span style="${PLACEHOLDER_HIGHLIGHT_STYLE}">[balance]</span>`,
};

const processHtmlContent = (content?: string): string => {
  if (!content) {
    return "";
  }

  // First, replace placeholders
  let processed = Object.entries(placeholderMap).reduce((acc, [key, replacement]) => {
    const regex = new RegExp(`\\{${key}\\}`, "gi");
    return acc.replace(regex, replacement);
  }, content);

  // Convert newlines to <br> tags for proper HTML rendering
  // Only convert \n that are not already inside HTML tags
  processed = processed.replace(/\n/g, '<br>');

  return processed;
};

const buildIframeDocument = (bodyContent: string): string => `<!DOCTYPE html>
<html>
  <head>
    <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline';" />
    <style>
      body { font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 12px; }
    </style>
  </head>
  <body>${bodyContent}</body>
</html>`;

export const HTMLPreview: React.FC<HTMLPreviewProps> = ({ htmlContent }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const processedHtml = useMemo(() => processHtmlContent(htmlContent), [htmlContent]);

  useEffect(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(buildIframeDocument(processedHtml));
    doc.close();
  }, [processedHtml]);

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-same-origin"
      title="html-preview"
      style={{ width: "100%", minHeight: 200, border: "1px solid #f0f0f0" }}
    />
  );
};
